import {
  CallbackInput,
  ConsultationDetails,
  ConsultationFactory,
  ConsultationInfo,
  ConsultationType,
  DoctorEndConsultAPIType,
  SentryMetadataParams,
  StatusType,
  TimerEvent,
  TypeOfRetry,
  doctorData,
  patientData,
} from "../libs/types";
import {
  differenceInYears,
  format,
  isAfter,
  isEqual,
  isValid,
  parseISO,
} from "date-fns";
import {
  MessagePayload /* MessageState, ReplyToPayload */,
  ReplyToPayload,
} from "./firebaseTypes";
import type { QueryResult } from "@apollo/client/react";
import { ApolloErrorOptions } from "../libs/interface";
import {
  ConsultationFormValues,
  PrescriptionFormValues,
  SpecialistReferralFormValues,
  TestReferralFormValues,
} from "../pages/doctor/ConcludeDoctorConsultation";
import {
  TMultipleTestVar,
  TPrescriptionVar,
  TSingleTestVar,
} from "../libs/conclude-doc-consult-types";
import { NavigateFunction } from "react-router-dom";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import { logException } from "../libs/sentry";
import { safeStorage } from "./safeStorage";
import { sessionStore } from "./sessionStore";

export const destoryStoredVariables = (consultationId?: string) => {
  const keys = [
    "rateConsultationStage",
    "waitingRoomStartTime",
    "consultationInfo",
    "providerLogo",
    "access_token",
    "consultationDetails",
    "agoraInfo",
    "selectedIssues",
    "createdThrough",
    "hotline",
    "whatsapp",
    "supportEmail",
    "retry",
    "retry_count",
    "triggeredBroadcast",
    "isRebroadcast",
    "consultationStartTime",
    "scheduledConsultationReachedTimer",
  ];
  keys.forEach((key) => {
    safeStorage.removeLocalItem(key);
    safeStorage.removeSessionItem(key);
  });

  if (consultationId) {
    safeStorage.removeLocalItem(`consultationStartTime_${consultationId}`);
    safeStorage.removeSessionItem(`access_token_${consultationId}`);
  }
};

export const emptyDeviceStorage = (consultationId?: string) => {
  destoryStoredVariables(consultationId);
};

export function safeArray<T>(prop: unknown): T[] {
  if (Array.isArray(prop)) {
    return prop as T[];
  } else {
    return [] as T[];
  }
}

export function safeString(prop: unknown, fallback = ""): string {
  if (typeof prop === "string") {
    return prop;
  } else {
    return fallback;
  }
}

export function safeNumber(prop: unknown, fallback = 0): number {
  if (typeof prop === "number" && !isNaN(prop)) {
    return prop;
  } else {
    return fallback;
  }
}

export function safeObject<T extends object>(
  prop: unknown,
  fallback = {} as T,
): T {
  if (prop !== null && typeof prop === "object" && !Array.isArray(prop)) {
    return prop as T;
  }
  return fallback;
}

export const formatName = (
  firstName: string | null,
  lastName: string | null,
): string => {
  const fName = firstName?.trim?.();
  const lName = lastName?.trim?.();

  if (fName && lName) {
    return `${fName} ${lName}`;
  }

  return fName || lName || "No name";
};

export const getInitials = (name: string) => {
  try {
    const splitedNamesArr = name.split(" ");

    const initailsArr = splitedNamesArr.map((name) => {
      const splitedNameArr = name.split("");
      return splitedNameArr[0];
    });

    return initailsArr.join("");
  } catch (error) {
    console.error("error from getInitials func.", error);
    return "";
  }
};

export const ConsultFactory: ConsultationFactory = (
  info: ConsultationDetails | undefined,
) => {
  if (!info)
    return {
      _id: null,
      conDate: "",
      conTime: "",
      consultationType: null,
      type: null,
      time: null,
      providerId: null,
      patient: {} as patientData,
      patientJoined: false,
      doctorJoined: false,
      doctor: {} as doctorData,
      contactMedium: null,
      isFollowUp: false,
      symptoms: [],
      isEnrollee: false,
      description: "",
      firstNotice: "",
      discomfortLevel: "None",
      status: null,
    };

  const infoDate = info?.createdAt;
  const type: ConsultationType = info?.type;
  const datePlusOneHour = info?.createdAt
    ? new Date((infoDate || "") as Date)
    : null;

  const conTime = datePlusOneHour ? `${format(datePlusOneHour, "p")}` : "";
  const conDate = datePlusOneHour
    ? `${format(datePlusOneHour, "ccc")} ${format(datePlusOneHour, "PPP")}`
    : "";
  //

  return {
    _id: info?._id as string,
    patient: safeObject<patientData>(info?.patient),
    conDate,
    conTime,
    consultationType: info?.type,
    type,
    time: info?.time,
    email: info?.patient?.email || "",
    providerId: info?.providerId,
    patientData: safeObject<patientData>(info?.patient),
    patientJoined: info?.patientJoined || false,
    doctorJoined: info?.doctorJoined || false,
    doctor: safeObject<doctorData>(info?.doctor),
    contactMedium: info?.contactMedium,
    isFollowUp: info?.isFollowUp || false,
    symptoms: info?.symptoms || [],
    isEnrollee: info?.isEnrollee || false,
    description: info?.description || "",
    firstNotice: info?.firstNotice || "",
    discomfortLevel: info?.discomfortLevel || "",
    status: info?.status || null,
  };
};

export const isConsultationTimeReached = (scheduledConsultationTime: Date) => {
  if (!isValid(scheduledConsultationTime)) return true;
  return (
    isEqual(Date.now(), scheduledConsultationTime) ||
    isAfter(Date.now(), scheduledConsultationTime)
  );
};

export const createConsultationVar = (
  prevConsultationInfo: ConsultationDetails,
  type: TypeOfRetry,
) => {
  const symptoms = (prevConsultationInfo?.symptoms || []).map((symptoms) => {
    return { name: symptoms.name };
  });
  const doctor =
    type === "same_doctor" ? prevConsultationInfo?.doctor?._id : null;

  const description = prevConsultationInfo?.description
    ? { description: prevConsultationInfo?.description }
    : {};
  return {
    consultationOwner: "Myself",
    patient: safeString(prevConsultationInfo?.patient?._id, ""),
    symptoms,
    discomfortLevel: prevConsultationInfo?.discomfortLevel,
    ...description,
    firstNotice: prevConsultationInfo?.firstNotice || "",
    type: prevConsultationInfo?.type,
    status: "pending",
    providerId: prevConsultationInfo?.providerId,
    contactMedium: prevConsultationInfo?.contactMedium,
    createdThrough: "api",
    doctor,
  };
};

export const isDevelopment = () => {
  return process.env.NODE_ENV === "development";
};

export const isImageUrl = (url: string) => {
  const regex = /^https?:\/\/.*\/.*\.(png|gif|webp|jpeg|jpg)\??.*$/gim;
  let result;
  const match = url?.match(regex);
  if (match && match?.length > 0) {
    result = true;
  } else {
    result = false;
  }
  return result;
};

export const isPdfUrl = (url: string) => {
  const regex = /^https?:\/\/.*\/.*\.(pdf)\??.*$/gim;
  return regex.test(url);
};

export function guessModeForPatientSwitch(
  mode: Record<
    "doctor" | "patient",
    null | "accepted" | "rejected" | "chat" | "video" | "voice"
  >,
) {
  const { doctor: doctorMode, patient: patientMode } = mode;

  if (doctorMode === "chat") {
    if (patientMode === null) return "request_for_chat";
    if (patientMode === "accepted") return "switch_to_chat";
  }

  if (doctorMode === "video") {
    if (patientMode === null) return "request_for_video";
    if (patientMode === "accepted") return "switch_to_video";
  }

  if (doctorMode === "voice") {
    if (patientMode === null) return "request_for_voice";
    if (patientMode === "accepted") return "switch_to_voice";
  }

  if (doctorMode === "accepted") {
    if (patientMode === "voice") return "switch_to_voice";
    if (patientMode === "video") return "switch_to_video";
    if (patientMode === "chat") return "switch_to_chat";
  }

  if (doctorMode === "rejected") {
    if (
      patientMode === "voice" ||
      patientMode === "video" ||
      patientMode === "chat" ||
      patientMode === null
    )
      return "declined";
  }

  if (doctorMode === null) {
    if (
      patientMode === "voice" ||
      patientMode === "video" ||
      patientMode === "chat"
    )
      return "requesting";
  }

  return "unknown";
}

export function guessModeForDoctorSwitch(
  mode: Record<
    "doctor" | "patient",
    null | "accepted" | "rejected" | "chat" | "video" | "voice"
  >,
) {
  const { doctor: doctorMode, patient: patientMode } = mode;

  if (patientMode === "chat") {
    if (doctorMode === null) return "request_for_chat";
    if (doctorMode === "accepted") return "switch_to_chat";
  }

  if (patientMode === "video") {
    if (doctorMode === null) return "request_for_video";
    if (doctorMode === "accepted") return "switch_to_video";
  }

  if (patientMode === "voice") {
    if (doctorMode === null) return "request_for_voice";
    if (doctorMode === "accepted") return "switch_to_voice";
  }

  if (patientMode === "accepted") {
    if (doctorMode === "voice") return "switch_to_voice";
    if (doctorMode === "video") return "switch_to_video";
    if (doctorMode === "chat") return "switch_to_chat";
  }

  if (patientMode === "rejected") {
    if (
      doctorMode === "voice" ||
      doctorMode === "video" ||
      doctorMode === "chat" ||
      doctorMode === null
    )
      return "declined";
  }

  if (patientMode === null) {
    if (
      doctorMode === "voice" ||
      doctorMode === "video" ||
      doctorMode === "chat"
    )
      return "requesting";
  }

  return "unknown";
}

export const sortByDate = (values: MessagePayload[]) => {
  try {
    return values.sort((current, prev) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const currentDate = new Date(current?.timestamp?.toDate()) as any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const prevDate = new Date(prev.timestamp.toDate()) as any;
      return currentDate - prevDate;
    });
  } catch (error) {
    console.error(error);
    return values;
  }
};

export const isMsgFromPatient = (message: MessagePayload, patientId: string) =>
  message.idFrom === patientId;

export const scrollToRepliedMsg = async (
  msgId: string,
  alignedLeft: boolean,
) => {
  try {
    const contentId = `msg_${msgId}`;
    const el = document.getElementById(contentId);
    const msgCont = document.getElementById("style-1");
    const className = alignedLeft ? "shake-left" : "shake-right";
    if (el) {
      msgCont?.scrollTo({
        top: el.offsetTop,
        behavior: "smooth",
      });

      el.classList.add(className);
      setTimeout(() => {
        el.classList.remove(className);
      }, 2000);
    }
  } catch (error) {
    console.error("couldn't scroll to message container", error);
  }
};

export const generateReplyToPayload = (
  replyTo: MessagePayload,
): ReplyToPayload => {
  return {
    _id: replyTo._id,
    content: replyTo.content,
    idFrom: replyTo.idFrom,
    timestamp: replyTo.timestamp,
  };
};

export const truncate = (string: string, length: number) => {
  const strArr = string.split("");
  if (strArr.length <= length) {
    return string;
  } else {
    return `${strArr.slice(0, length).join("")}...`;
  }
};

//GET ACCESS TOKEN AND STORE IT
export const getAccess = async (
  getAccessToken: () => Promise<
    QueryResult<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      {
        consultationId: string | undefined;
      }
    >
  >,
  getErrorMsg: (error: ApolloErrorOptions) => string | string[] | undefined,
  consultationId: unknown,
  isExternal: boolean,
) => {
  const externalToken = import.meta.env.VITE_EXT_KEY;
  const id = `${consultationId}`;
  if (isExternal && externalToken && consultationId) {
    sessionStore.setAccessToken(id, `${externalToken}`);
    return;
  }

  const res = await getAccessToken();
  if (res.error) {
    const errMsg =
      getErrorMsg(res.error as unknown as ApolloErrorOptions) ||
      "Something went wrong!";
    throw Error(`${errMsg}`);
  }
  if (res.data && consultationId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const accessToken = (res.data as Record<string, any>).accessConsultation
      ?.account?.access_token;
    sessionStore.setAccessToken(id, `${accessToken}`);
  }
};

export function generateSentryMetadata({
  consultationId,
  actor,
  action,
  source = "useSyncConsultationState", // "useNotifyConsultation",
  additionalContext = {},
}: SentryMetadataParams) {
  return {
    tags: {
      consultationId,
      actor,
      action,
    },
    contexts: {
      consultation: {
        id: consultationId,
        actor,
        action,
        source,
        ...additionalContext,
      },
    },
  };
}

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const getAutoCompleteStyles = (
  borderColor: string,
  disabled: boolean,
) => ({
  "& .MuiInputBase-root": {
    height: "48px !important",
    color: "#334D57 !important",
    backgroundColor: disabled ? "#00212D0D" : "white",
    border: `1px solid rgba(128, 128, 128, 0.25)`,
    borderRadius: "8px",
    paddingRight: "10px !important",
    paddingLeft: "10px !important",
    display: "flex",
    alignItems: "center",
    "&.Mui-focused": {
      boxShadow: `0 0 0 1px ${borderColor}`,
    },
  },
  "& .MuiAutocomplete-endAdornment": {
    display: "flex",
  },
  "& .MuiOutlinedInput-root": {
    padding: "0px",
    "& input": {
      padding: "0px",
      border: "none",
      height: "30px",
      fontSize: { xs: "16px", sm: "16px", md: "16px", lg: "12px", xl: "12px" },
      "&:focus": {
        outline: "none", // Ensures no outline when focused
      },
    },
    "& button": {
      all: "unset",
      backgroundColor: "transparent", // MUI Autocomplete button background
      color: borderColor,
      cursor: "pointer",
    },
    "& :disabled": {
      color: "#334D57 !important",
    },
    "& fieldset": {
      border: "none", // Remove initial border
    },
    "&:hover fieldset": {
      borderColor: "#e2e8f0", // Change border color on hover
    },
    "&.Mui-focused fieldset": {
      border: "none", // Ensures no outline border when focused
    },
  },
});

export const generateAddDocConsultation = (
  consultationDetails: ConsultationInfo,
  formData: ConsultationFormValues,
) => {
  return {
    consultationId: consultationDetails?._id,
    doctor: consultationDetails?.doctor?._id,
    diagnosis: formData?.diagnoses,
    doctorNote: formData?.doctorsNote,
  };
};

export const transformTestsVar = (
  tests: TestReferralFormValues[],
  type: DoctorEndConsultAPIType,
) => {
  const safeTests = safeArray<TestReferralFormValues>(tests);

  if (type === "MULTIPLE")
    return safeTests.map((test) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _id, ...rest } = test;
      return {
        ...rest,
      };
    }) as TMultipleTestVar[];

  return safeTests.map((test) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { note, _id, ...rest } = test;
    return {
      ...rest,
      notes: note,
    };
  }) as TSingleTestVar[];
};

const transformDrugFrequencyToNum = (frequency: string) => {
  const matchingObj = {
    "1x daily (Od)": 1,
    "2x daily (Bd)": 2,
    "3x daily (Tds)": 3,
    "4x daily (Qds)": 4,
  };
  return safeNumber(matchingObj[frequency as keyof typeof matchingObj], 0);
};

export const transformFormPrescriptionVar = (
  prescriptions: PrescriptionFormValues[],
) => {
  const transformedPrescriptions = prescriptions.map((prescription) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customDrugForm, timing, duration, _id, packSize, ...rest } =
      prescription;
    const formDrugFrom = prescription?.drugForm;
    const drugForm =
      formDrugFrom === "others" ? safeString(customDrugForm) : formDrugFrom;
    const customPackSize = packSize && packSize > 0 ? { packSize } : {};

    return {
      ...rest,
      drugForm,
      ...customPackSize,
      amount: prescription.drugPrice,
      dosageFrequency: {
        timing: transformDrugFrequencyToNum(timing),
        duration,
      },
    };
  }) as TPrescriptionVar;

  return transformedPrescriptions;
};

export const transformDoctorConcludeConsultationEntries = (
  type: DoctorEndConsultAPIType,
  prescriptions: PrescriptionFormValues[],
  tests: TestReferralFormValues[],
  specialistRef: SpecialistReferralFormValues[],
) => {
  const prescriptionsVar = transformFormPrescriptionVar(prescriptions);

  const testsRefVar = transformTestsVar(tests, type);

  const specialistRefVar =
    safeArray<SpecialistReferralFormValues>(specialistRef);

  return {
    prescriptions: prescriptionsVar,
    testsRef: testsRefVar,
    specialistRef:
      type === "MULTIPLE"
        ? specialistRefVar.map((item) => item.specialization)
        : specialistRefVar,
  };
};

export const dobToAgeString = (dob: unknown, fallback = "No DOB"): string => {
  try {
    const date = dob instanceof Date ? dob : parseISO(safeString(dob));

    if (!isValid(date)) {
      throw new Error("Invalid date");
    }

    const age = differenceInYears(new Date(), date);

    return age >= 0 ? `${age}yrs` : fallback;
  } catch (error) {
    console.error("Error calculating age from DOB:", error);
    return fallback;
  }
};

/**
 * Generates timer events at specified intervals
 * @param totalMinutes - Total countdown time in minutes (e.g., 4 for 4 minutes)
 * @param intervalMinutes - Interval between events (1 for every minute, 0.5 for every 30 seconds)
 * @param callbacks - Single callback function or array of callback functions
 * @returns Array of TimerEvent objects
 */
export const generateTimerEvents = (
  totalMins: number,
  intervalMins: number,
  callbacks: CallbackInput,
) => {
  const intervals = Math.floor(totalMins / intervalMins);
  const events: TimerEvent[] = [];

  for (let index = 0; index < intervals; index++) {
    const timeRemaining =
      safeNumber(totalMins, 4.5) - safeNumber(intervalMins, 0.5) * index;
    if (timeRemaining <= 0) continue;
    const callback = Array.isArray(callbacks) ? callbacks[index] : callbacks;
    events.push({
      timeRemaining: timeRemaining * 60 * 1000,
      callback:
        callback !== undefined
          ? callback
          : () => console.error("No function Found!!!"),
    });
  }

  return events;
};

export const redirectToConsultationRoom = async (
  consultationInfo: ConsultationInfo,
  FSConsultationMedium: string | undefined,
  navigateFN: NavigateFunction,
  type: "doctor" | "patient",
  communicationEnded: boolean,
) => {
  const consultationId = consultationInfo?._id;
  const doctorId = consultationInfo?.doctor?._id;
  const patientId = consultationInfo?.patient?._id;
  const contactMedium = consultationInfo?.contactMedium;
  const roomId = `${consultationId}_${doctorId}_${patientId}`;
  const status = safeString(
    consultationInfo?.status,
  ).toLowerCase?.() as StatusType;

  const fromApp = { state: { fromApp: true } };

  // If the session has already ended, route directly to the post-consultation
  // flow before any status or ID validation — the patient/doctor must never
  // land in the consultation room after endConsultation is true.
  if (communicationEnded) {
    if (!consultationId)
      throw Error(
        "Consultation ID not found while routing to post-consultation flow",
      );
    safeStorage.removeSessionItem("waitingRoomStartTime");
    if (type === "patient") {
      navigateFN(`/rate-consultation/${consultationId}`, fromApp);
      return;
    }
    if (type === "doctor") {
      navigateFN(`/doctor/conclude-communication/${consultationId}`, fromApp);
      return;
    }
  }

  // DO NOT TOUCH!!!
  const canEnterConsultationRoom =
    status === "ongoing" || status === "accepted";

  if (!canEnterConsultationRoom) {
    const err = new Error(
      `Consultation with status of "${status}" cannot enter the consultation room.`,
    );
    logException(
      err,
      safeString(consultationId),
      type,
      "Redirect to consultation room",
      "From redirectToConsultationRoom function",
    );
    throw err;
  }

  if (!consultationId)
    throw Error(
      "Consultation ID not found while redirecting to consultation room",
    );
  if (!doctorId)
    throw Error("Doctor ID not found while redirecting to consultation room");
  if (!patientId)
    throw Error("Patient ID not found while redirecting to consultation room");

  const medium =
    FSConsultationMedium && FSConsultationMedium !== "NO-MEDIUM"
      ? FSConsultationMedium
      : contactMedium;

  const urlPrefix = type === "doctor" ? "/doctor" : "";

  safeStorage.removeSessionItem("waitingRoomStartTime");

  if (medium === "chat") {
    navigateFN(`${urlPrefix}/chat/${roomId}`, fromApp);
    return;
  }
  if (medium === "voice") {
    navigateFN(`${urlPrefix}/voice/${consultationId}`, fromApp);
    return;
  }
  if (medium === "video") {
    navigateFN(`${urlPrefix}/video/${consultationId}`, fromApp);
    return;
  }

  if (medium !== "video" && medium !== "voice" && medium !== "chat") {
    navigateFN(`${urlPrefix}/chat/${roomId}`, fromApp);
    return;
  }
};

export function formatPhoneNumber(
  phone: string,
  country: CountryCode = "NG",
): { local: string; international: string } {
  try {
    const parsed = parsePhoneNumberFromString(phone, country);

    if (parsed && parsed.isValid()) {
      return {
        local: parsed.formatNational(), // e.g. (0703) 123 4567
        international: parsed.number.replace("+", ""), // e.g. 2347031234567
      };
    }

    // Parsed but invalid
    console.error(
      `[Phone Format Error] Invalid number after parsing: "${phone}" (${country}). Falling back to original.`,
    );

    const international = phone.replace("+", "");
    return { local: phone, international };
  } catch (error) {
    // Parsing threw an exception
    console.error(
      `[Phone Format Error] Could not parse phone number: "${phone}" (${country}). Falling back to original.`,
      error,
    );

    if (!phone) return { local: "", international: "" };

    const international = phone.replace("+", "");
    return { local: phone, international };
  }
}

export const toTitleCase = (str: string) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getConsultationIdFromPath = (path: string) => {
  // Handle doctor routes with consultationId
  const doctorConsultationMatch = path.match(
    /\/doctor\/(?:video|voice|waiting|conclude-communication|conclude-consultation|review-recommendations)\/([^/]+)$/,
  );
  if (doctorConsultationMatch && doctorConsultationMatch[1]) {
    return doctorConsultationMatch[1];
  }

  // Handle doctor waiting room: /doctor/:consultationId
  const doctorWaitingMatch = path.match(/\/doctor\/([^/]+)$/);
  if (doctorWaitingMatch && doctorWaitingMatch[1]) {
    return doctorWaitingMatch[1];
  }

  // Handle chat routes (both patient and doctor): /chat/:roomId or /doctor/chat/:roomId
  // Extract consultationId from roomId (assumes format: consultationId_otherId)
  const chatMatch = path.match(/\/(?:doctor\/)?chat\/([^/]+)$/);
  if (chatMatch && chatMatch[1]) {
    return chatMatch[1].split("_")[0];
  }

  // Handle video/voice routes: /video/:consultationId or /voice/:consultationId
  const mediaMatch = path.match(/\/(?:video|voice)\/([^/]+)$/);
  if (mediaMatch && mediaMatch[1]) {
    return mediaMatch[1];
  }

  // Handle rate consultation: /rate-consultation/:consultationId
  const rateMatch = path.match(/\/rate-consultation\/([^/]+)$/);
  if (rateMatch && rateMatch[1]) {
    return rateMatch[1];
  }

  // Handle after consultation: /after-consultation/:consultationId
  const afterMatch = path.match(/\/after-consultation\/([^/]+)$/);
  if (afterMatch && afterMatch[1]) {
    return afterMatch[1];
  }

  // Handle retry: /retry/:consultationId
  const retryMatch = path.match(/\/retry\/([^/]+)$/);
  if (retryMatch && retryMatch[1]) {
    return retryMatch[1];
  }

  // Handle patient waiting room: /:consultationId (catch-all for root-level paths)
  const waitingMatch = path.match(/^\/([^/]+)$/);
  if (waitingMatch && waitingMatch[1] && waitingMatch[1] !== "success") {
    return waitingMatch[1];
  }

  return null;
};

export const checkConsultationMedium = (
  FSConsultationMedium: string,
  consultationMedium: string,
) => {
  if (
    !FSConsultationMedium ||
    FSConsultationMedium === "NO-MEDIUM" ||
    FSConsultationMedium === ""
  )
    return consultationMedium;
  return FSConsultationMedium;
};
